import axios from 'axios'
import * as types from '../mutation-types'
import {find, get, has, isArray, isObject, orderBy} from 'lodash';
import bugsnagService from '../../services/bugsnagService';
import userPermissionService from '../../services/userPermissionService';
import localForage from 'localforage';
import teamService from '../../../../ml/services/teamService';
import userService from '../../../../ml/services/userService';

// state
export const state = {
  csrfToken: window.config ? window.config.csrfToken : null,
  team: null,
  teamsLoading: false, // MLD-6470
  teams: [], // MLD-6470
  user: null,
  permissions: userPermissionService.getDefaultPermissions(),
};

// mutations
export const mutations = {
  setCsrfToken(state, csrfToken) { // MLD-4934
    state.csrfToken = csrfToken;
  },

  [types.FETCH_USER_SUCCESS](state, user) {
    state.user = user
    bugsnagService.setUser(user); // MLD-5357
  },

  setTeam(state) { // MLD-7272 This needs to be called after fetchTeams and fetchUser.
    if (isObject(state.user) && isArray(state.teams) && state.teams.length > 0) {
      state.team = find(state.teams, t => t.id === state.user.current_team_id);
    } else {
      state.team = null;
    }
  },

  [types.FETCH_USER_FAILURE](state) {
    state.team = null;
    state.user = null;
    bugsnagService.setUser(null);
  },

  [types.LOGOUT](state) {
    state.team = null;
    state.user = null;
    window.localStorage.clear();
    window.sessionStorage.clear();
    bugsnagService.setUser(null);
  },

  setTeamsLoading(state, teamsLoading) { // MLD-6470
    state.teamsLoading = teamsLoading;
  },
  setTeams(state, teams) { // MLD-6470
    state.teams = teams;
  },

  [types.UPDATE_USER](state, {user}) {
    state.user = user
  },
  setPermissions(state, permissions) { // MLD-5518
    state.permissions = permissions;
  },
};

// actions
export const actions = {
  async fetchPermissions({commit, dispatch}) { // MLD-5518
    try {
      const permissions = await userPermissionService.getPermissions();
      commit('setPermissions', permissions);
    } catch {
      commit('setPermissions', userPermissionService.getDefaultPermissions());
    }
  },
  async fetchTeams({commit}) { // MLD-6470
    try {
      console.log('fetchTeams start');
      commit('setTeamsLoading', true);
      const teams = await teamService.getCurrentTeams();
      commit('setTeams', orderBy(teams, ['slug'], ['asc']));
      console.log('fetchTeams end');
      return true;
    } catch (e) {
      return false;
    } finally {
      commit('setTeamsLoading', false);
    }
  },
  async fetchUser({commit, dispatch}) {
    try {
      const user = await userService.getCurrentUser();
      commit(types.FETCH_USER_SUCCESS, user)
      commit('setPermissions', user.permissions); // MLD-5518
      try {
        dispatch('initEcho', user);
      } catch (e) {
        console.log('initEcho exception', e);
      }
      return true;
    } catch (e) {
      commit(types.FETCH_USER_FAILURE);
      return false;
    }
  },

  async updateUser({commit}, payload) {
    commit(types.UPDATE_USER, payload)
  },

  async logout({commit, dispatch}) {
    try {
      await localForage.clear();
      await axios.post('/logout')
      dispatch('clearSavedState'); // MLD-6444
    } catch (e) {
      console.log('logout failed pushing to login', e.message);
    } finally {
      commit(types.LOGOUT);
      window.location.assign('/login');
    }
  }
};

// getters
export const getters = {
  authenticated: state => isObject(state.user) && isObject(state.team),
  isAuthenticated: state => isObject(state.user) && isObject(state.team),
  canTeamUseSinch: (state, getters) => { // MLD-7490
    return getters.team
      && getters.team.sinch_enabled;
  },
  canSendMessages: (state, getters) => { // MLD-7490
    return getters.canTeamUseSinch
    && getters.user.can_use_sinch;
  },
  canUseSinch: (state, getters) => { // MLD-7490
    return getters.canTeamUseSinch
      && getters.user.can_use_sinch;
  },
  canTeamQuickShareLink: (state, getters) => { // MLD-7622
    return getters.team
      && getters.team.sharing_enabled
      && getters.team.quickshare_link_enabled
  },
  canTeamUseSnippets: (state, getters) => { // MLD-3900
    return getters.team
      && getters.team.sharing_enabled
      && getters.team.sending_emails_enabled
      && getters.team.members_can_send_emails
      && getters.team.snippets_enabled;
  },
  isUserInCountry: (state, getters) => (countryID) => { // MLD-4074
    return getters
      && getters.user
      && isObject(getters.user.country)
      && getters.user.country.id === countryID;
  },
  isUserInUnitedStates: (state, getters) => { // MLD-4074
    return getters.isUserInCountry('US');
  },
  isFullUser: (state, getters) => {
    return getters
      && getters.user
      && getters.user.is_full_user;
  },
  canScheduleEmails: (state, getters) => { // MLD-6106
    return getters?.team?.schedule_emails_enabled;
  },
  canTeamUseSpaces: (state, getters) => { // MLD-5030
    return getters?.team?.spaces_enabled;
  },
  canUserCreateSpaces: (state, getters) => { // MLD-5030
    return getters?.canTeamUseSpaces
      && getters?.user?.can_create_spaces;
  },
  canTeamUseHaystack: (state, getters) => { // MLD-7250
    return getters
      && getters.team?.haystack_enabled;
  },
  canUserUseHaystack: (state, getters) => { // MLD-7250
    return getters?.canTeamUseHaystack
      && getters.user?.can_use_haystack;
  },
  canUserShareSpaces: (state, getters) => { // MLD-5030
    return getters
      && getters.team?.spaces_enabled
      && getters.user?.can_share_spaces;
  },
  canUsePresentationNotes: (state, getters) => { // MLD-6435
    return getters
      && getters.team?.presentation_notes_enabled;
  },
  canUseSnippets: (state, getters) => { // MLD-3900
    return getters.canTeamUseSnippets
      && getters.isFullUser
      && getters.user.can_use_snippets;
  },
  canVerifyEmails: (state, getters) => { // MLD-6016
    return getters.team?.email_verification_enabled
      && getters.isFullUser
      && getters.user.can_verify_emails;
  },
  canManageShareTemplates: state => { // MLD-3150
    if (!state.team) {
      return false;
    }
    if (!state.team.member_share_templates) {
      return false;
    }
    if (!state.user) {
      return false;
    }
    if (!state.user.is_full_user) {
      return false;
    }
    return state.user.can_manage_share_templates;
  },
  canEmailSelf: state => { // MLD-5505
    if (!state.team) {
      return false;
    }
    return state.user.can_email_self; // MLD-5562
    //return state.team.users_can_email_themselves;
  },
  canUseBuilder: (state, getters) => { // MLD-2882
    if (!getters.team) {
      return false;
    }
    if (!getters.user) {
      return false;
    }
    if (!getters.team.builder_enabled) {
      return false;
    }
    if (!getters.user.is_full_user) {
      return false;
    }
    return getters.user.can_use_builder;
  },
  canComposeEmails: (state, getters) => { // MLD-6403
    if (getters.canSendEmail && getters.isCampaignsEnabled && getters.user && getters.user.can_compose_emails) {
      return true;
    }
    return getters.isEmailComposerEnabled;
  },
  canCreateContact: (state) => { // MLD-2598
    if (state.user && state.user.team && state.user.team.is_member) {
      return state.team.members_can_create_contacts;
    }
    if (state.team) { // MLD-4583
      return state.team.admins_can_create_contacts;
    }
    return false;
  },
  canCopyEmailLists: (state, getters) => { // MLD-5213
    if (getters.team && getters.user) {
      return getters.team.campaigns_enabled
        && getters.user.can_copy_email_lists;
    }
    return false;
  },
  canManageDeviceSessions: (state, getters) => { // MLD-3922
    if (getters.team && [1, 199].includes(getters.team.id) && getters.isFullUser) {
      if (getters.team.id === 199) {
        return getters.isUserInUnitedStates;
      } else if (getters.team.id === 1) {
        return true;
      }
    }
    return false;
  },
  canUseContacts: (state, getters) => { // MLD-3922
    return getters && getters.isFullUser;
  },
  canUseDashboard: (state, getters) => { // MLD-3922
    return getters && getters.isFullUser;
  },
  canSendEmail: (state, getters) => { // MLD-3917
    return getters.team
      && getters.team.sharing_enabled
      && getters.team.sending_emails_enabled
      && getters.team.members_can_send_emails
      && getters.isFullUser;
  },
  canUseSentEmails: (state, getters) => {
    return getters.isFullUser;
  },
  faxingEnabled: (state, getters) => { // MLD-3982
    return getters.team
      && getters.team.fax_enabled;
  },
  canSendFaxes: (state, getters) => { // MLD-3298
    return getters.faxingEnabled
      && getters.isFullUser
      && getters.user.can_send_faxes;
  },
  canSyncContacts: (state, getters) => { // MLD-2598
    if (!getters.team.crm_enabled || !getters.team.crm_provider) {
      return false;
    }
    if (getters.user && getters.user.import_crm_contacts === false) { // MLD-3570
      return false;
    }
    if (getters.user && getters.user.team && getters.user.team.is_member) {
      return getters.team.members_can_sync_contacts;
    }
    return getters.team.admins_can_sync_contacts;
  },
  canUploadContacts: state => state.team && state.team.upload_user_contacts_enabled, // MLD-2867
  crmLabel: state => state.team && state.team.crm_name ? state.team.crm_name : 'CRM', // MLD-2598
  csrfToken: state => state.csrfToken,
  currentUser: state => state.user,
  impersonate: state => { // MLD-1841
    return window.config ? window.config.impersonate : null;
  },
  impersonating: state => { // MLD-3014
    if (window.config && window.config.impersonate) {
      return !!window.config.impersonate.is_impersonating;
    }
    return false;
  },
  isAdmin: state => state.user && state.user.team ? state.user.team.is_admin : false,
  isEmployee: state => state.user ? state.user.is_employee : false,
  isMember: state => state.user && state.user.team ? state.user.team.is_member : false,
  isSuperAdmin: state => state.user ? state.user.is_super_admin : false,
  role: state => state.user && state.user.team ? state.user.team.role : null,
  team: state => state.team,
  isTeamUsingLegacyEmailComposer: (state, getters) => { // MLD-7126
    return getters.team
      && getters.team.email_composer_enabled === false;
  },
  isEmailComposerEnabled: (state, getters) => { // MLD-6403
    return getters.team
      && getters.team.email_composer_enabled;
  },
  isCampaignsEnabled: (state, getters) => { // MLD-3917
    return getters.team && getters.team.campaigns_enabled;
  },
  canUseCampaign: (state, getters) => { // MLD-3917
    return getters.isCampaignsEnabled && getters.user && getters.user.is_full_user;
  },
  isEngagementScoreEnabled: (state, getters) => { // MLD-5226
    return getters.team && getters.team.engagement_score_enabled;
  },
  isQuickShareEnabled: (state, getters) => { // MLD-3917
    return getters.team && (getters.team.quickshare_app_enabled || getters.team.quickshare_browser_enabled);
  },
  hasMedicalAttributes(state, getters) { // MLD-3435
    return getters.team
      && !!getters.team.user_contact_medical_fields_enabled
  },
  isUserContactLeadSourceEnabled: (state, getters) => { // MLD-6827
    return getters.team && getters.team.user_contact_lead_source_enabled;
  },
  isUserContactLevelEnabled: (state, getters) => { // MLD-4405
    return getters.team && getters.team.user_contact_level_enabled;
  },
  isUserContactSegmentEnabled: (state, getters) => { // MLD-6140
    return getters.team && getters.team.user_contact_segment_enabled;
  },
  isUserContactTierEnabled: (state, getters) => { // MLD-6140
    return getters.team && getters.team.user_contact_tier_enabled;
  },
  isUserContactTypeEnabled: (state, getters) => { // MLD-8030
    return getters.team && getters.team.user_contact_type_enabled;
  },
  isUserContactMDMEnabled: (state, getters) => { // MLD-4391
    return getters.team && getters.team.user_contact_mdm_enabled;
  },
  isUserContactTerritoryEnabled: (state, getters) => { // MLD-4749
    return getters.team && getters.team.user_contact_territory_enabled;
  },
  isOnOtherTeams: (state, getters) => { // MLD-3800
    if (getters.activeTeams.length > 1) {
      return true;
    }
    return false;
  },
  activeTeams: (state, getters) => { // MLD-3800
    return getters.teams.filter(t => t.is_active && t.is_license_active);
  },
  otherTeams: (state, getters) => {
    return getters.activeTeams.filter(t => t.id !== getters.user.current_team_id);
  },
  isPipedriveTokenSet: (state, getters) => { // MLD-7203
    return getters.user?.has_pipedrive_api_token;
  },
  teamUsesPipedrive: (state, getters) => { // MLD-7203
    return getters.team?.crm_provider === 'pipedrive';
  },
  teamsLoading: state => state.teamsLoading,
  teams: (state) => { // MLD-3800
    if (isArray(state.teams) && state.teams.length > 0) { // MLD-6470
      return state.teams;
    }
    /*if (isObject(state.user) && isArray(state.user.teams)) {
      return state.user.teams;
    }*/
    return [];
  },
  user: state => state.user,
  canTeamUseMetabase: (state, getters) => { // MLD-4575
    return window.config
      && !!window.config.metabaseEnabled
      && getters.team
      && getters.team.metabase_enabled;
  },
  canUseMetabase: (state, getters) => { // MLD-4575
    return getters.canTeamUseMetabase
      && getters.user
      && getters.user.can_use_metabase;
  },
  emailPreviewsEnabled: (state, getters) => { // MLD-4550
    return getters.team
      && getters.team.email_previews_enabled;
  },
  spamTestEnabled: (state, getters) => { // MLD-4012
    return window.config
      ? window.config.spamTestEnabled
      : false;
  },
  // MLD-5518
  permissions: (state) => {
    return state.permissions
  },
  hasPermission: (state, getters) => (key) => {
    return (getters && isObject(getters.permissions) && has(getters.permissions, key))
      ? get(getters.permissions, key)
      : false;
  },
  canEditBrands: (state, getters) => {
    return getters.team?.brands_enabled
      && getters.hasPermission('can_edit_brands');
  },
  canEditDevices: (state, getters) => {
    return getters.hasPermission('can_edit_devices');
  },
  canEditEmailTemplates: (state, getters) => {
    return getters.team?.sharing_enabled
      && getters.hasPermission('can_edit_email_templates');
  },
  canTestEmailTemplates: (state, getters) => { // MLD-4550
    return getters.emailPreviewsEnabled
      && getters.hasPermission('can_test_email_templates');
  },
  canEditEvents: (state, getters) => {
    return getters.team?.events_enabled
      && getters.hasPermission('can_edit_events');
  },
  canEditFaxes: (state, getters) => {
    return getters.team?.fax_enabled
      && getters.hasPermission('can_edit_faxes');
  },
  canEditFiles: (state, getters) => {
    return getters.team?.content_enabled
      && getters.hasPermission('can_edit_files');
  },
  canEditFolders: (state, getters) => {
    return getters.team?.content_enabled
      && getters.hasPermission('can_edit_folders');
  },
  canEditForms: (state, getters) => {
    return getters.team?.forms_enabled
      && getters.hasPermission('can_edit_forms');
  },
  canEditGroups: (state, getters) => {
    return getters.hasPermission('can_edit_groups');
  },
  canEditLabels: (state, getters) => {
    return getters.team?.content_enabled
      && getters.hasPermission('can_edit_labels');
  },
  canEditMessages: (state, getters) => {
    return getters.team?.messages_enabled
      && getters.hasPermission('can_edit_messages');
  },
  canEditMetabasePermissions: (state, getters) => {
    return getters.team?.metabase_enabled
      && getters.hasPermission('can_edit_metabase_permissions');
  },
  canEditPresentations: (state, getters) => {
    return getters.team?.content_enabled
      && getters.hasPermission('can_edit_presentations');
  },
  canEditProducts: (state, getters) => {
    return getters.team?.products_enabled
      && getters.hasPermission('can_edit_products');
  },
  canEditSubscribers: (state, getters) => {
    return getters.team?.sharing_enabled
      && getters.hasPermission('can_edit_subscribers');
  },
  canEditTeam: (state, getters) => {
    return getters.hasPermission('can_edit_team');
  },
  canEditTerritories: (state, getters) => {
    return getters.team?.territories_enabled
      && getters.hasPermission('can_edit_territories');
  },
  canEditUserContacts: (state, getters) => {
    return getters.hasPermission('can_edit_user_contacts');
  },
  canEditUsers: (state, getters) => {
    return getters.hasPermission('can_edit_users');
  },
  canEditWebhooks: (state, getters) => {
    return getters.team?.webhooks_enabled
      && getters.hasPermission('can_edit_webhooks');
  },
  canExportData: (state, getters) => {
    return getters.hasPermission('can_export_data');
  },
  canScanBusinessCards: (state, getters) => { // MLD-6878
    return getters.team?.scanner_enabled
      && getters.user?.scanner_enabled;
  }
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations
}
